import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { baseCustomersQueryKey } from "@/lib/api/customer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { CrmRecordType } from "@/lib/api/crm/masters";
import type { CrmRules } from "@/lib/api/crm/rules";
import { RecordFormSkeleton } from "../form/components/RecordFormPageLayout";
import { RecordForm } from "../form/RecordForm";
import type { RecordFormValues } from "../form/recordFormSchema";
import { useRecordCreator, type RecordCreatorOptions } from "../form/useRecordCreator";
import { CrmDialogLoadError } from "./CrmDialogLoadError";

/**
 * Creates a customer type CRM record (which creates its customer), in a dialog, into a chosen record group
 * or a new one. The nx customer lists are reloaded after the create. Mount it only while open.
 */
export function CrmCustomerCreateDialog({
  title,
  description,
  header,
  initialValues,
  recordTypeFilter,
  onClose,
  onCreated,
  validate,
}: {
  title: string;
  description: ReactNode;
  /** Shown between the title and the form. */
  header?: ReactNode;
  /** What the form starts with (rules are loaded by then). */
  initialValues: (rules: CrmRules) => RecordFormValues;
  /** Narrows the customer types offered. */
  recordTypeFilter?: (rules: CrmRules, recordType: CrmRecordType) => boolean;
  onClose: () => void;
  onCreated: RecordCreatorOptions["onCreated"];
  validate?: RecordCreatorOptions["validate"];
}) {
  const queryClient = useQueryClient();
  const creator = useRecordCreator({
    presetGroupId: null,
    onCreated: (result) => {
      void queryClient.invalidateQueries({ queryKey: baseCustomersQueryKey });
      onCreated(result);
    },
    validate,
  });

  return (
    <Dialog open onOpenChange={(nextOpen: boolean) => { if (!nextOpen) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg md:max-w-3xl lg:max-w-5xl" data-testid="crm-customer-create-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {header}
        {creator.status === "loading" && <RecordFormSkeleton />}
        {creator.status === "error" && (
          <CrmDialogLoadError subject={creator.subject} isRetrying={creator.isRetrying} onRetry={creator.retry} />
        )}
        {creator.status === "ready" && (
          <RecordForm
            mode="create"
            rules={creator.rules}
            defaultValues={initialValues(creator.rules)}
            groupRecords={creator.group?.records ?? []}
            groupName={creator.group?.long_name ?? null}
            lockedFields={{ master_id: false }}
            allowNewGroup
            allowedRecordTypes={customerRecordTypes(creator.rules, recordTypeFilter)}
            onGroupChange={creator.onGroupChange}
            onSubmit={creator.onSubmit}
            onCancel={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The record types GET /crm/rules marks creates_customer, narrowed by the caller's filter. */
function customerRecordTypes(
  rules: CrmRules,
  recordTypeFilter: ((rules: CrmRules, recordType: CrmRecordType) => boolean) | undefined,
): CrmRecordType[] {
  return (Object.keys(rules.record_types) as CrmRecordType[]).filter((recordType) =>
    rules.record_types[recordType].creates_customer && (recordTypeFilter?.(rules, recordType) ?? true));
}
